import React, { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tooltip } from "primereact/tooltip";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useHttpGets, httpPost } from "../../../services/httpService";
import { MEMBER_KEY, Messages } from "../../../utils/constants";
import { ApiEndPoints } from "../../../utils/endpoints";
import { IMemberProfile, IMyEntityAndGoals } from "../../../interface/myEntityAndGoals";
import { REIDropDown, REIProgressSpinner, REIToastContainer } from "../../../components/ui";
import { formatCurrency, formatCurrencyWithDecimal, formatNumberWithDecimal } from "../../../utils/commonUtil";

const UserProfiles: React.FC<{ profiles: IMyEntityAndGoals; entityId: string; handleRefreshData: () => void }> = ({
  profiles,
  handleRefreshData,
}) => {
  const memberId = localStorage.getItem(MEMBER_KEY);
  const [userProfiles, setUserProfiles] = useState<IMemberProfile[]>([]);
  const [newProfile, setNewProfile] = useState<IMemberProfile | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [memberProfiles, setMemberProfiles] = useState<IMemberProfile[]>([]);
  const [memberProfile, setMemberProfile] = useState<number>();
  const toastRef = useRef<Toast>(null);
  const editTooltipRef = useRef<Tooltip>(null);
  const [isNewRow, setIsNewRow] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    name: string;
    income: string;
    owns: string;
  }>({
    name: "",
    income: "",
    owns: "",
  });

  useEffect(() => {
    setLoading(true);
    setUserProfiles(profiles?.MemberProfiles || []);
    useHttpGets(`${ApiEndPoints.tracker}/GetMemberProfiles/${memberId}`)
      .then((result) => {
        if (result?.data && !result.error) {
          setMemberProfiles(result.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching member profiles:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [profiles]);

  useEffect(() => {
    if (memberProfile) {
      const selectedMember = memberProfiles.find((x) => x.MemberProfileId === memberProfile);
      if (selectedMember) {
        setNewProfile({
          MemberName: selectedMember.MemberName,
          Income: selectedMember.Income,
          PortionOwned: Number(selectedMember.PortionOwned),
          MemberProfileId: memberProfile,
          MemberEntityId: parseInt(profiles.EntityId, 10),
        });
        const nameError = validateName(selectedMember.MemberName);
        const incomeError = validateIncome(selectedMember.Income);
        const ownsError = validateOwns(selectedMember.PortionOwned);

        setValidationErrors({
          name: nameError,
          income: incomeError,
          owns: ownsError,
        });
      }
    } else if (memberProfile == 0 && newProfile) {
      setNewProfile({ MemberEntityId: 0, MemberName: "", Income: "0", PortionOwned: 0, MemberProfileId: 0 });
    }
  }, [memberProfile, memberProfiles, profiles.EntityId]);

  const handleAdd = () => {
    setNewProfile({ MemberEntityId: 0, MemberName: "", Income: "0", PortionOwned: 0, MemberProfileId: 0 });
    setValidationErrors({ name: "", income: "", owns: "" });
    setIsNewRow(true);
    const nameError = validateName("");
    const incomeError = validateIncome("0");
    const ownsError = validateOwns(0);

    setValidationErrors({
      name: nameError,
      income: incomeError,
      owns: ownsError,
    });
  };

  const updateMemberEntity = (updatedUserProfile: IMemberProfile[]) => {
    const profilesUnchanged = JSON.stringify(userProfiles) === JSON.stringify(updatedUserProfile);

    if (profilesUnchanged) {
      return;
    }
    setLoading(true);
    if (!updatedUserProfile) return;
    httpPost(`${ApiEndPoints.tracker}MemberEntity/Update/`, {
      CountryCode: profiles.CountryCode,
      EntityName: profiles.EntityName,
      EntityTypeCode: profiles.EntityTypeCode,
      GoalAfterTaxCashFlow: profiles.GoalsTotal.Target.PostTaxCashflow,
      GoalAnnualRent: profiles.GoalsTotal.Target.AnnualRent,
      GoalDesc: profiles.Goals,
      GoalEquityUnlocked: profiles.GoalsTotal.Target.Equity,
      GoalPortfolioValue: profiles.GoalsTotal.Target.PortfolioValue,
      GoalPreTaxCashFlow: profiles.GoalsTotal.Target.PreTaxCashflow,
      MemberEntityId: profiles?.EntityId,
      MemberId: memberId,
      MemberProfiles: updatedUserProfile,
      Note: "",
    })
      .then((data) => {
        if (data.status === 200) {
          toastRef.current?.show({
            severity: "success",
            summary: Messages.SUCCESS,
            detail: "Member entity updated successfully",
            life: 2000,
          });
        } else {
          toastRef.current?.show({
            severity: "error",
            summary: Messages.ERROR,
            detail: "Failed to update member entity",
            life: 2000,
          });
        }
      })
      .catch((error) => {
        toastRef.current?.show({
          severity: "error",
          summary: Messages.ERROR,
          detail: "Error updating member entity",
          life: 2000,
        });
      })
      .finally(() => {
        setLoading(false);
        setNewProfile(null);
        setUserProfiles([]);
        handleRefreshData();
      });
  };

  const getNextAvailableName = (baseName: string): string => {
    const names = memberProfiles.map((item) => item.MemberName);

    // If base name is not taken, return it directly
    if (!names.includes(baseName)) {
      return baseName;
    }

    // Otherwise, check for the next available "baseName (n)"
    let index = 1;
    while (names.includes(`${baseName} (${index})`)) {
      index++;
    }

    return `${baseName} (${index})`;
  };

  const handleSave = (updatedRows: IMemberProfile[]) => {
    if (!validateNewProfile()) {
      return;
    }

    if (updatedRows) {
      const profileData = [...userProfiles, { ...newProfile }];
      const totalOwns = profileData.reduce((sum, profile) => sum + (profile?.PortionOwned ?? 0), 0);
      if (totalOwns > 100) {
        toastRef.current?.show({
          severity: "error",
          summary: Messages.ERROR,
          detail: "Total ownership percentage cannot exceed 100%",
          life: 2000,
        });
        return;
      }
      const existingProfile = memberProfiles.find(
        (profile) =>
          profile.MemberName === newProfile?.MemberName && profile.MemberProfileId !== newProfile?.MemberProfileId
      );

      if (existingProfile) {
        confirmDialog({
          message: (
            <div className="text-lg">
              You already have a profile called {existingProfile?.MemberName} attached to this entity, with an income of{" "}
              {formatCurrency(Number(existingProfile?.Income) || 0)}. Would you like to use this record instead or add a
              new record?
            </div>
          ),
          acceptLabel: "Add new profile",
          rejectLabel: "Use existing profile",
          accept: () => {
            const nextAvailableName = getNextAvailableName(newProfile?.MemberName || "");
            updateMemberEntity([
              ...updatedRows,
              {
                MemberProfileId: 0,
                MemberName: nextAvailableName,
                Income: newProfile?.Income || "",
                PortionOwned: newProfile?.PortionOwned || 0,
                MemberEntityId: undefined,
              },
            ]);
          },
          reject: () => {
            const userProfile = userProfiles.find((profile) => profile.MemberName === newProfile?.MemberName);
            const nextAvailableName = getNextAvailableName(newProfile?.MemberName || "");
            if (!userProfile) {
              updateMemberEntity([
                ...updatedRows,
                {
                  MemberProfileId: existingProfile.MemberProfileId,
                  MemberName: newProfile?.MemberName || nextAvailableName,
                  Income: existingProfile.Income,
                  PortionOwned: newProfile?.PortionOwned || 0,
                  MemberEntityId: undefined,
                },
              ]);
            }
          },
        });
      } else {
        updateMemberEntity([
          ...updatedRows,
          {
            MemberProfileId: newProfile?.MemberProfileId || 0,
            MemberName: newProfile?.MemberName ?? "",
            Income: newProfile?.Income || "",
            PortionOwned: newProfile?.PortionOwned || 0,
            MemberEntityId: 0,
          },
        ]);
      }
    }
  };

  const handleRemove = (id: number) => {
    confirmDialog({
      message: <div className="text-lg">Are you sure you want to remove this profile from the entity?</div>,
      header: "Delete Confirmation",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        if (id !== 0 && userProfiles.length > 1) {
          const updatedProfiles = userProfiles.filter((profile) => profile.MemberProfileId !== id);
          updateMemberEntity(updatedProfiles);
        }
      },
    });
  };

  const handleEdit = (updatedRows: IMemberProfile[]) => {
    const invalidRow = updatedRows.find((profile) => !profile?.PortionOwned || profile?.PortionOwned <= 0);
    if (invalidRow) {
      toastRef.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Owns (%) is required and must be greater than 0.",
        life: 2000,
      });
      return;
    }
    const totalOwns = updatedRows.reduce((sum, profile) => sum + (profile?.PortionOwned ?? 0), 0);
    if (totalOwns > 100) {
      toastRef.current?.show({
        severity: "error",
        summary: Messages.ERROR,
        detail: "Total ownership percentage cannot exceed 100%",
        life: 2000,
      });
      return;
    }
    updateMemberEntity(updatedRows);
  };

  const handleCancel = () => {
    setNewProfile(null);
    setEditingId(null);
    setValidationErrors({ name: "", income: "", owns: "" });
    setMemberProfile(0);
    setIsNewRow(false);
  };

  const validateName = (name: string): string => {
    if (!name || name.trim() === "") {
      return "Name is required";
    }
    return "";
  };

  const validateIncome = (income: string | number): string => {
    const incomeValue = Number(income);
    if (!income || isNaN(incomeValue) || incomeValue <= 0) {
      return "Income must be 0 or greater";
    }
    return "";
  };

  const validateOwns = (owns: number): string => {
    if (!owns || owns <= 0) {
      return "Owns (%) must be greater than 0";
    }
    if (owns > 100) {
      return "Owns (%) cannot exceed 100";
    }
    return "";
  };

  const validateNewProfile = (): boolean => {
    const nameError = validateName(newProfile?.MemberName || "");
    const incomeError = validateIncome(newProfile?.Income || "");
    const ownsError = validateOwns(newProfile?.PortionOwned || 0);

    setValidationErrors({
      name: nameError,
      income: incomeError,
      owns: ownsError,
    });

    return nameError === "" && incomeError === "" && ownsError === "";
  };

  const renderNewRow = () => {
    return (
      <>
        <DataTable
          value={newProfile ? [newProfile] : []}
          className="p-datatable-sm"
          rowClassName={(rowData) => "align-top"}
        >
          <Column
            header="Name"
            body={() => (
              <div>
                <InputText
                  id="megup-txt-name"
                  data-testid="megup-txt-name"
                  placeholder="Name"
                  value={newProfile?.MemberName || ""}
                  onChange={(e) => {
                    const newName = e.target.value;
                    setNewProfile({ ...newProfile!, MemberName: newName });

                    const nameError = validateName(newName);
                    setValidationErrors((prev) => ({ ...prev, name: nameError }));
                  }}
                  className={`w-full ${validationErrors.name != "" ? "border-red-500 border-1" : "border-gray-300"}`}
                  disabled={memberProfile && memberProfile > 0 && !isNewRow ? true : false}
                />
                {validationErrors.name && (
                  <small className="text-red-500 text-sm mt-1 block">{validationErrors.name}</small>
                )}
              </div>
            )}
          />
          <Column
            header="Income ($)"
            body={() => (
              <div>
                <InputNumber
                  id="megup-num-income"
                  data-testid="megup-num-income"
                  placeholder="Income ($)"
                  maxFractionDigits={2}
                  min={0}
                  value={Number(newProfile?.Income) || 0}
                  onChange={(e) => {
                    let newIncome = e.value ?? 0;
                    setNewProfile({ ...newProfile!, Income: newIncome.toString() });

                    const incomeError = validateIncome(newIncome);
                    setValidationErrors((prev) => ({ ...prev, income: incomeError }));
                  }}
                  className="w-full"
                  invalid={validationErrors.income != ""}
                />
                {validationErrors.income && (
                  <small className="text-red-500 text-sm mt-1 block">{validationErrors.income}</small>
                )}
              </div>
            )}
          />
          <Column
            header="Owns (%)"
            body={() => (
              <div>
                <InputNumber
                  id="megup-num-owns"
                  data-testid="megup-num-owns"
                  placeholder="Owns (%)"
                  maxFractionDigits={2}
                  value={newProfile?.PortionOwned || 0}
                  min={0}
                  onChange={(e) => {
                    const newOwns = e.value || 0;
                    setNewProfile({ ...newProfile!, PortionOwned: newOwns });

                    const ownsError = validateOwns(newOwns);
                    setValidationErrors((prev) => {
                      return { ...prev, owns: ownsError };
                    });
                  }}
                  className="w-full"
                  invalid={validationErrors.owns != ""}
                />
                {validationErrors.owns && (
                  <small className="text-red-500 text-sm mt-1 block">{validationErrors.owns}</small>
                )}
              </div>
            )}
          />
          <Column
            header="Actions"
            body={() => (
              <div className="flex gap-2">
                <Button
                  id="megup-btn-save"
                  data-testid="megup-btn-save"
                  label="Save"
                  icon="pi pi-check"
                  className="w-full"
                  disabled={
                    validationErrors.name !== "" ||
                    validationErrors.income !== "" ||
                    validationErrors.owns !== "" ||
                    !newProfile?.MemberName ||
                    !newProfile?.Income ||
                    !newProfile?.PortionOwned
                  }
                  onClick={() => {
                    if (!newProfile) return;
                    handleSave(userProfiles);
                    setMemberProfile(0);
                  }}
                />
                <Button
                  id="megup-btn-cancel"
                  data-testid="megup-btn-cancel"
                  label="Cancel"
                  icon="pi pi-times"
                  className="w-full"
                  onClick={handleCancel}
                />
              </div>
            )}
          />
        </DataTable>
      </>
    );
  };

  const renderExistingRows = () => {
    return (
      <>
        <Tooltip ref={editTooltipRef} target=".p-row-editor-init" content="Edit row" position="right" />
        {editingId !== null && (
          <>
            <Tooltip target=".p-row-editor-save" content="Save changes" position="right" />
            <Tooltip target=".p-row-editor-cancel" content="Cancel editing" position="right" />
          </>
        )}
        <div className="altRowShade">
          <DataTable
            value={userProfiles}
            className="p-datatable-sm"
            tableStyle={{ minWidth: "50rem" }}
            dataKey="MemberProfileId"
            editMode="row"
            onRowEditInit={(e) => {
              setEditingId(e.data.MemberProfileId);
              editTooltipRef.current?.hide();
            }}
            onRowEditCancel={() => setEditingId(null)}
            onRowEditSave={(e) => {
              if (!e.data) return;
              const updatedProfiles = userProfiles.map((profile) =>
                profile.MemberProfileId === e.data.MemberProfileId ? { ...profile, ...e.newData } : profile
              );
              handleEdit(updatedProfiles);
            }}
          >
            <Column field="MemberName" header="Name" style={{ width: "25%" }} />
            <Column
              field="Income"
              header="Income ($)"
              body={(rowData) => formatCurrencyWithDecimal(Number(rowData.Income))}
              style={{ width: "25%" }}
              editor={(options) => (
                <InputNumber
                  id={`megup-num-income-${options.rowData.MemberProfileId}`}
                  data-testid={`megup-num-income-${options.rowData.MemberProfileId}`}
                  value={Number(options.value)}
                  maxFractionDigits={2}
                  onValueChange={(e) => options.editorCallback?.((e.value || 0).toString())}
                  className="w-full"
                />
              )}
            />
            <Column
              field="PortionOwned"
              header="Owns (%)"
              style={{ width: "25%" }}
              body={(rowData) => formatNumberWithDecimal(Number(rowData.PortionOwned), 2, 2)}
              editor={(options) => (
                <div>
                  <InputNumber
                    id={`megup-num-owns-${options.rowData.MemberProfileId}`}
                    data-testid={`megup-num-owns-${options.rowData.MemberProfileId}`}
                    maxFractionDigits={2}
                    value={options.value}
                    onValueChange={(e) => options.editorCallback?.(e.value || 0)}
                    className={`w-full ${!options.value || options.value <= 0 ? "border-red-500 text-red-500" : ""}`}
                  />
                </div>
              )}
            />
            <Column
              rowEditor
              className="actions-cell"
              header="Actions"
              style={{ width: "12.5%" }}
              bodyStyle={{ textAlign: "center" }}
              headerStyle={{ textAlign: "center", justifyItems: "center" }}
            />
            {userProfiles.length > 1 && (
              <Column
                header="Remove"
                body={(rowData) => (
                  <Button
                    id={`megup-btn-remove-${rowData.MemberProfileId}`}
                    data-testid={`megup-btn-remove-${rowData.MemberProfileId}`}
                    icon="pi pi-trash"
                    className="actions-cell p-button-sm p-button-danger"
                    tooltip="Remove profile"
                    onClick={() => handleRemove(rowData.MemberProfileId)}
                    title="Remove profile"
                  />
                )}
                bodyStyle={{ textAlign: "center", width: "250px" }}
                headerStyle={{ justifyItems: "center" }}
              />
            )}
          </DataTable>
        </div>
      </>
    );
  };

  return (
    <div className="border-solid border border-gray-200 mt-4">
      <ConfirmDialog />
      <REIToastContainer toastReference={toastRef} />
      <div className="items-start p-4 border-b-2 text-start">
        <h1 className="text-2xl font-bold">USER PROFILES FOR THIS ENTITY</h1>
        <p>User profiles for this entity</p>
        <span className="italic ">
          This is important for calculating tax implications accurately across your portfolio. Please ensure all
          profiles have a unique name.
        </span>
      </div>
      <div className="gap-3 p-4">
        {loading && (
          <div className="text-center">
            <REIProgressSpinner></REIProgressSpinner>
          </div>
        )}
        {!loading && userProfiles.length > 0 && renderExistingRows()}
        <h4 className="text-left">Add another profile</h4>
        <div className="text-start flex flex-col md:flex-row gap-3 m-4">
          {memberProfiles.filter(
            (profile) => !userProfiles.some((user) => user.MemberProfileId === profile.MemberProfileId)
          ).length > 0 &&
            !loading && (
              <>
                <div className="flex-0 w-1/3">
                  <label>Select an existing profile</label>

                  <REIDropDown
                    id="megup-drop-profile"
                    data={[
                      { MemberProfileId: 0, MemberName: "Please select a profile" },
                      ...memberProfiles.filter(
                        (profile) => !userProfiles.some((user) => user.MemberProfileId === profile.MemberProfileId)
                      ),
                    ]}
                    value={memberProfile}
                    onSelectionChange={(e) => {
                      setMemberProfile(e.value);
                      setIsNewRow(false);
                      setValidationErrors({ name: "", income: "", owns: "" });
                    }}
                    label="MemberName"
                    optionValue="MemberProfileId"
                    placeholder="Please select a profile"
                  />
                </div>
                <div className="self-center">OR</div>
              </>
            )}
          {!loading && (
            <Button
              id="megup-btn-addprofile"
              data-testid="megup-btn-addprofile"
              label="Add a new profile to this entity"
              icon="pi pi-plus"
              className="mt-4"
              onClick={handleAdd}
            />
          )}
        </div>
      </div>
      {newProfile != null && renderNewRow()}
    </div>
  );
};

export default UserProfiles;
